import { useEffect, useState } from "react";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useSmartDeviceFlow from "../../../../hooks/useSmartDeviceFlow";
import useSmartDeviceFormData from "../../../../hooks/useSmartDeviceFormData";
import {
  DEVICE_TYPES,
  IMEI_INPUT_METHODS,
} from "../../../../pages/smartDeviceInsurancePages/newSmartDevicePolicy/data";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import SmartDeviceJourneyLayout from "../../layouts/SmartDeviceJourneyLayout";
import DeviceForm from "./DeviceForm";
import DeviceTypeSelector from "./DeviceTypeSelector";
import IMEIInputMethod from "./IMEIInputMethod";

// Form constant
export const SMART_DEVICE_SELECTION_FORM = "SMART_DEVICE_SELECTION_FORM";

const DeviceSelection = ({ onClose }) => {
  const { handleNextSubstep } = useSmartDeviceFlow();
  const translate = useLanguageTranslate();

  // State management - Separate data for each input method
  const [formData, setFormData] = useState({
    deviceType: "",
    imeiInputMethod: IMEI_INPUT_METHODS.MANUAL, // Default for smartphones
    // Screenshot method data
    screenshot: {
      imeiScreenshot: null,
      imeiNumber: "",
      brand: "",
      model: "",
    },
    // Manual method data
    manual: {
      imeiNumber: "",
      brand: "",
      model: "",
    },
  });
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  // Redux form data persistence
  const { saveSmartDeviceDataInRedux, redux_form_data } =
    useSmartDeviceFormData(SMART_DEVICE_SELECTION_FORM, setFormData);

  // Load saved data on mount
  useEffect(() => {
    if (redux_form_data) {
      // Check if data is in new nested format (has screenshot and manual objects)
      if (redux_form_data.screenshot && redux_form_data.manual) {
        // New nested format - use as is
        setFormData(redux_form_data);
      } else {
        // Old flat format - migrate to nested based on current method
        const method = redux_form_data.imeiInputMethod || IMEI_INPUT_METHODS.MANUAL;
        const isScreenshot = method === IMEI_INPUT_METHODS.SCREENSHOT;

        setFormData({
          deviceType: redux_form_data.deviceType || "",
          imeiInputMethod: method,
          // Put data in the active method's tab
          screenshot: isScreenshot ? {
            imeiScreenshot: redux_form_data.imeiScreenshot || null,
            imeiNumber: redux_form_data.imeiNumber || "",
            brand: redux_form_data.brand || "",
            model: redux_form_data.model || "",
          } : {
            imeiScreenshot: null,
            imeiNumber: "",
            brand: "",
            model: "",
          },
          manual: !isScreenshot ? {
            imeiNumber: redux_form_data.imeiNumber || "",
            brand: redux_form_data.brand || "",
            model: redux_form_data.model || "",
          } : {
            imeiNumber: "",
            brand: "",
            model: "",
          },
        });
      }
    }
  }, []);

  // Helper to get current active method data
  const getCurrentMethodData = () => {
    const method = formData.imeiInputMethod === IMEI_INPUT_METHODS.SCREENSHOT ? 'screenshot' : 'manual';
    return formData[method];
  };

  // Handle device type selection
  const handleDeviceSelect = (deviceType) => {
    setFormData((prev) => ({
      ...prev,
      deviceType,
      // Reset IMEI method to manual when changing device type
      imeiInputMethod: IMEI_INPUT_METHODS.MANUAL,
      // Reset both tabs when changing device type
      screenshot: {
        imeiScreenshot: null,
        imeiNumber: "",
        brand: "",
        model: "",
      },
      manual: {
        imeiNumber: "",
        brand: "",
        model: "",
      },
    }));
    // Clear device type error when selected
    setErrors((prev) => ({ ...prev, deviceType: "" }));
  };

  // Handle IMEI input method selection (only for smartphones)
  const handleMethodSelect = (method) => {
    setFormData((prev) => ({
      ...prev,
      imeiInputMethod: method,
    }));
    // Clear errors when switching tabs
    setErrors({});
  };

  // Handle form field changes - update the correct tab's data
  const handleFormDataChange = (field, value) => {
    const method = formData.imeiInputMethod === IMEI_INPUT_METHODS.SCREENSHOT ? 'screenshot' : 'manual';

    setFormData((prev) => ({
      ...prev,
      [method]: {
        ...prev[method],
        [field]: value,
      },
    }));
    // Clear error for this field when user makes changes
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    const currentData = getCurrentMethodData();

    // Device type is required
    if (!formData.deviceType) {
      newErrors.deviceType = translate(
        "Please select a device type",
        "الرجاء اختيار نوع الجهاز"
      );
    }

    // IMEI validation (only validate when using manual method for smartphones)
    if (formData.deviceType === DEVICE_TYPES.SMARTPHONE &&
        formData.imeiInputMethod === IMEI_INPUT_METHODS.MANUAL) {
      if (!currentData.imeiNumber || currentData.imeiNumber.trim() === "") {
        newErrors.imeiNumber = translate(
          "This field is required.",
          "هذا الحقل مطلوب"
        );
      } else if (currentData.imeiNumber.length !== 15) {
        newErrors.imeiNumber = translate(
          "IMEI must be exactly 15 digits.",
          "يجب أن يتكوّن رقم IMEI من 15 رقمًا فقط."
        );
      } else if (!/^\d+$/.test(currentData.imeiNumber)) {
        newErrors.imeiNumber = translate(
          "IMEI number must contain only digits.",
          "يجب أن يحتوي رقم IMEI على أرقام فقط"
        );
      }
    }

    // Screenshot validation (if screenshot method is selected)
    if (
      formData.deviceType === DEVICE_TYPES.SMARTPHONE &&
      formData.imeiInputMethod === IMEI_INPUT_METHODS.SCREENSHOT &&
      !currentData.imeiScreenshot
    ) {
      newErrors.imeiScreenshot = translate(
        "This field is required.",
        "هذا الحقل مطلوب"
      );
    }

    // Brand is required
    if (!currentData.brand || currentData.brand === "") {
      newErrors.brand = translate("This field is required.", "هذا الحقل مطلوب");
    }

    // Model is required
    if (!currentData.model || currentData.model === "") {
      newErrors.model = translate("This field is required.", "هذا الحقل مطلوب");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle continue
  const handleContinue = async () => {
    if (validateForm()) {
      try {
        const currentData = getCurrentMethodData();
        // Save both flattened structure (for API compatibility) and nested structure (for tab persistence)
        const dataToSave = {
          deviceType: formData.deviceType,
          imeiInputMethod: formData.imeiInputMethod,
          // Flattened data at top level for backward compatibility and quote API
          imeiScreenshot: currentData.imeiScreenshot || null,
          imeiNumber: currentData.imeiNumber || "",
          brand: currentData.brand || "",
          model: currentData.model || "",
          // Nested data for both tabs to preserve data when switching
          screenshot: formData.screenshot,
          manual: formData.manual,
        };

        // Save combined data to Redux
        await saveSmartDeviceDataInRedux(dataToSave);

        // Navigate to next step
        handleNextSubstep();
      } catch (error) {
        console.error("Error saving device selection data:", error);
      }
    } else {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const errorElement = document.querySelector(
          `[name="${firstErrorField}"]`
        );
        errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  // Determine if continue button should be disabled
  const isContinueDisabled = () => {
    // Always disabled if no device type selected or processing
    if (!formData.deviceType || isProcessing) return true;

    const currentData = getCurrentMethodData();
    // Run validation and check if there are any errors
    const newErrors = {};

    // IMEI validation for smartphones (only validate manual entry when using manual method)
    if (formData.deviceType === DEVICE_TYPES.SMARTPHONE &&
        formData.imeiInputMethod === IMEI_INPUT_METHODS.MANUAL) {
      if (!currentData.imeiNumber || currentData.imeiNumber.trim() === "") {
        newErrors.imeiNumber = true;
      } else if (currentData.imeiNumber.length !== 15) {
        newErrors.imeiNumber = true;
      } else if (!/^\d+$/.test(currentData.imeiNumber)) {
        newErrors.imeiNumber = true;
      }
    }

    // Screenshot validation for smartphones using screenshot method
    if (
      formData.deviceType === DEVICE_TYPES.SMARTPHONE &&
      formData.imeiInputMethod === IMEI_INPUT_METHODS.SCREENSHOT &&
      !currentData.imeiScreenshot
    ) {
      newErrors.imeiScreenshot = true;
    }

    // Brand validation
    if (!currentData.brand || currentData.brand.trim() === "") {
      newErrors.brand = true;
    }

    // Model validation
    if (!currentData.model || currentData.model.trim() === "") {
      newErrors.model = true;
    }

    return Object.keys(newErrors).length > 0;
  };

  // Check if device type is smartphone
  const isSmartphone = formData.deviceType === DEVICE_TYPES.SMARTPHONE;

  return (
    <SmartDeviceJourneyLayout is_hide_back_btn={false}>
      <BottomStickyBtnLayout
        btn_onClick={handleContinue}
        btn_label={translate("Continue", "التالي")}
        btn_disabled={isContinueDisabled()}
      >
        <div className="lg:w-[50%] 2xl:w-[33%] max767:w-full mx-auto ipad:w-[65%] pb-5 max767:px-4">
          <h1 className="text-[#1A2024] text-center text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] max767:text-left  max767:rtl:text-right mb-2">
            {translate("About your device", "حول جهازك")}
          </h1>
          <p className="text-[#1A2024] text-center text-base not-italic font-normal leading-6 tracking-[-0.16px] mb-6 max767:text-left  max767:rtl:text-right">
            {translate(
              "Please enter the required information below.",
              "الرجاء إدخال المعلومات المطلوبة أدناه."
            )}
          </p>

          {/* Device Type Selector - Always visible */}
          <DeviceTypeSelector
            selectedDevice={formData.deviceType}
            onDeviceSelect={handleDeviceSelect}
          />
          {errors.deviceType && (
            <p className="text-red-500 text-sm mt-1 mb-4">
              {errors.deviceType}
            </p>
          )}

          {/* IMEI Input Method - Only show for smartphones */}
          {isSmartphone && (
            <IMEIInputMethod
              selectedMethod={formData.imeiInputMethod}
              onMethodSelect={handleMethodSelect}
            />
          )}

          {/* Device Form - Only show after device type is selected */}
          {formData.deviceType && (
            <DeviceForm
              deviceType={formData.deviceType}
              imeiMethod={formData.imeiInputMethod}
              formData={getCurrentMethodData()}
              onFormDataChange={handleFormDataChange}
              errors={errors}
              isProcessing={isProcessing}
              onProcessingChange={setIsProcessing}
            />
          )}
        </div>
      </BottomStickyBtnLayout>
    </SmartDeviceJourneyLayout>
  );
};

export default DeviceSelection;
